/**
 * 管理员动态口令绑定页（见方案 2B.2 的第一道防线）。
 *
 * 规则很硬：**未绑定动态口令时，管理权限完全不生效**。即使密码泄漏，
 * 攻击者登录后也只能看到这一页 —— 它拿不到任何管理接口的响应。
 *
 * 顺序：POST /auth/totp/setup 拿密钥与 otpauth URL（后端顺带渲染好二维码 data URI）
 *      → 用认证器 App 扫码 → POST /auth/totp/verify 提交 6 位动态码 → 刷新 /auth/me。
 *
 * 正常路径下这一页根本不会出现：数据库约束 ck_auth_user_admin_needs_totp
 * 保证 system_admin 一定有 totp_enabled_at。它是"万一约束被绕过"时的兜底，
 * 所以宁可多写一页，也不要让一个没开二次验证的管理员进到面板里。
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardHeader } from '../../components/ui/Card';
import { BuildStamp } from '../../components/layout/BuildStamp';
import { useAuth } from '../../lib/auth';
import * as adminApi from '../../lib/api/admin';
import { useToast } from '../../components/ui/Toast';
import { describeFailureMessage } from '../../lib/api/failure';
import { ADMIN_TOTP_FAILED, totpCodeIssue } from '../../lib/adminAuth';

export function AdminTotpPage() {
  const { reload, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [secret, setSecret] = useState<string | null>(null);
  const [otpauthUrl, setOtpauthUrl] = useState<string | null>(null);
  const [qrSvgDataUrl, setQrSvgDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 进页面就拿一份新的待绑定密钥（旧的在 KV 里 10 分钟过期，重复调用只会覆盖）
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi
      .beginTotpSetup()
      .then((data) => {
        if (cancelled) return;
        setSecret(data.secret);
        setOtpauthUrl(data.otpauthUrl);
        setQrSvgDataUrl(data.qrSvgDataUrl);
      })
      .catch((err: unknown) => {
        // 统一出口：后端校验失败/5xx 时 `message` 可能是一串 JSON（见 lib/api/failure.ts），
        // 这里只显示人话，拿不到时用「绑定失败，请重试」。
        if (!cancelled) setError(describeFailureMessage(err, ADMIN_TOTP_FAILED));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    // 前端先说一遍：6 位数字是后端 totpVerifySchema 的硬要求，省一次注定 400 的往返。
    // 空 / 位数不对时**不发请求**，也就不会把后端的校验 JSON 端到用户脸上。
    const issue = totpCodeIssue(code);
    if (issue !== null) {
      setError(issue);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await adminApi.confirmTotpSetup(code.trim());
      await reload();
      toast.showToast('动态口令已绑定', 'success');
      navigate('/admin', { replace: true });
    } catch (err) {
      // 同上：统一出口，绝不显示原始 JSON。
      setError(describeFailureMessage(err, ADMIN_TOTP_FAILED));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 px-4 py-10">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-5 text-center text-white">
          <span aria-hidden="true" className="text-3xl">
            🔐
          </span>
          <h1 className="mt-2 text-xl font-bold">先绑定动态口令</h1>
          <p className="mt-1 text-sm text-slate-400">
            未绑定动态口令的管理员无法使用任何管理功能；绑定前这个页面是唯一的入口。
          </p>
        </div>

        <Card>
          <CardHeader
            title="1. 用认证器 App 扫码"
            description="Google Authenticator / Microsoft Authenticator / 1Password 等均可"
          />
          {loading && <p className="text-sm text-slate-500">正在生成密钥…</p>}
          {qrSvgDataUrl && (
            <img
              src={qrSvgDataUrl}
              alt="动态口令二维码"
              width={220}
              height={220}
              className="mx-auto rounded-xl border border-slate-200 bg-white p-2"
            />
          )}
          {!loading && !qrSvgDataUrl && (
            <p className="text-sm text-amber-700">
              二维码未能生成（服务端二维码组件不可用）。请在认证器 App 里选择
              “手动输入密钥”，粘贴下面这串密钥。
            </p>
          )}
          {secret && (
            <div className="mt-3 space-y-1.5 rounded-xl bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-500">密钥（手工输入时用）</p>
              <code className="block break-all text-sm font-semibold text-slate-900">{secret}</code>
              {otpauthUrl && (
                <>
                  <p className="pt-1 text-xs font-medium text-slate-500">
                    otpauth URL（在浏览器地址栏打开也会显示二维码）
                  </p>
                  <code className="block break-all text-[11px] text-slate-500">{otpauthUrl}</code>
                </>
              )}
            </div>
          )}
        </Card>

        <div className="mt-4">
          <Card>
            <CardHeader title="2. 输入 App 上的 6 位动态码" />
            <form onSubmit={onSubmit} className="space-y-4">
              <Input
                label="动态口令"
                name="totpCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
              />
              {error && (
                <div role="alert" className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
                  {error}
                </div>
              )}
              <Button type="submit" fullWidth size="lg" loading={submitting} disabled={code.length !== 6}>
                完成绑定
              </Button>
            </form>
            <button
              type="button"
              onClick={() => void logout().then(() => navigate('/admin/login', { replace: true }))}
              className="mt-3 w-full rounded-xl px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100"
            >
              退出登录
            </button>
          </Card>
        </div>

        {/*
          「构建于 …」版本标记（2026-10-16 补）。

          这一页是独立全屏布局（不套 AdminShell），但它是管理端的**硬门禁页**：
          未绑定动态口令的管理员只会看到它。在这里显示构建版本，与 AdminShell、
          家长端同源同格式（同一个 `BuildStamp` / 同一个 `__BUILD_TIME__`），
          不含任何账号或家庭数据。
        */}
        <BuildStamp className="mt-4 text-center" />
      </div>
    </div>
  );
}
